'use client';

import React, { forwardRef, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { useBuyerProductStore } from 'app/store/product';
import i18n from '@shared/i18n';
import { FEATURE_FLAG_KEYS } from 'app/shop-ui/constants';
import { replacePlaceholder } from 'libs/replacePlaceholder';
import { SSR } from './ssr';
import STYLE from './ProductPromoInfo.module.scss';

interface ProductPromoInfoProps extends BasicShopUIPropTypes, BuyerLayoutProps<Product> {
    // 自行加入其他 props
    content: string;
}

const DefaultPromoInfo = ({
    enableContent,
    content,
}: {
    enableContent: boolean;
    content: string;
}) => {
    return (
        <>
            {enableContent && <h3 className={STYLE.productPromoInfoTitle}>{content}</h3>}
            <ul className={STYLE.productPromoInfoList}>
                <li className={STYLE.productPromoInfoItem}>
                    <span
                        className={clsx(
                            STYLE.productPromoInfoItemBadge,
                            STYLE.promoBadge
                        )}
                    >
                        {i18n.gettext('優惠')}
                    </span>
                    <p className={STYLE.productPromoInfoItemContent}>
                        {i18n.gettext('【活動名稱】全館商品滿 NT$ 3,000 折 NT$ 300')}
                    </p>
                </li>
                <li className={STYLE.productPromoInfoItem}>
                    <span
                        className={clsx(
                            STYLE.productPromoInfoItemBadge,
                            STYLE.giveawayBadge
                        )}
                    >
                        {i18n.gettext('贈品')}
                    </span>
                    <p className={STYLE.productPromoInfoItemContent}>
                        {i18n.gettext('消費滿 NT$ 2,500 即可獲得贈品')}
                    </p>
                </li>
                <li className={STYLE.productPromoInfoItem}>
                    <span
                        className={clsx(
                            STYLE.productPromoInfoItemBadge,
                            STYLE.shippingBadge
                        )}
                    >
                        {i18n.gettext('運費')}
                    </span>
                    <p className={STYLE.productPromoInfoItemContent}>
                        {i18n.gettext('【活動名稱】全館消費訂單免運')}
                    </p>
                </li>
            </ul>
        </>
    );
};

interface BuyerPromoInfoProps {
    promos: ProductPromos | null;
    enableContent: boolean;
    content: string;
    highlightColor: string;
}

const BuyerPromoInfo: React.FC<BuyerPromoInfoProps> = (props) => {
    const { promos, enableContent, content, highlightColor } = props;

    if (!promos || !promos.length) {
        return null;
    }

    const getPromoType = (type: string, subject: string) => {
        if (type === 'promo' && subject === 'shipping') {
            return 'shipping';
        }

        return type;
    };

    const promoBadgeMap = {
        promo: STYLE.promoBadge,
        giveaway: STYLE.giveawayBadge,
        shipping: STYLE.shippingBadge,
        coupon: STYLE.promoBadge,
    };

    const promoContentMap = {
        promo: i18n.gettext('優惠'),
        giveaway: i18n.gettext('贈品'),
        shipping: i18n.gettext('運費'),
        coupon: i18n.gettext('優惠'),
    };

    return (
        <>
            {enableContent && <h3 className={STYLE.productPromoInfoTitle}>{content}</h3>}
            <ul className={STYLE.productPromoInfoList}>
                {promos.map((promo, idx) => {
                    const type = getPromoType(promo.type, promo.subject);

                    return (
                        <li
                            key={promo.id + promo.type + idx}
                            className={STYLE.productPromoInfoItem}
                        >
                            <span
                                className={clsx(
                                    STYLE.productPromoInfoItemBadge,
                                    promoBadgeMap[type]
                                )}
                            >
                                {promoContentMap[type]}
                            </span>
                            <p className={STYLE.productPromoInfoItemContent}>
                                {promo.is_display_name && promo.name + ' '}
                                {replacePlaceholder(
                                    promo.promotional_copy.text,
                                    promo.promotional_copy.placeholder,
                                    highlightColor
                                )}
                            </p>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

export const ProductPromoInfo = forwardRef<HTMLDivElement, ProductPromoInfoProps>(
    (props, ref) => {
        const { className, style, styleMap, content, isBuyer, __ssr } = props;

        const [hydrated, setHydrated] = useState(false);

        const enableContent = !!styleMap?.[FEATURE_FLAG_KEYS.ENABLE_CONTENT];

        const highlightColor = styleMap?.[FEATURE_FLAG_KEYS.FONT_COLOR] || 'inherit';

        const { product } = useBuyerProductStore();

        const promoInfo = isBuyer ? product?.promos ?? null : null;

        useEffect(() => {
            setHydrated(true);
        }, []);

        if (!hydrated && isBuyer) {
            return <SSR.ProductPromoInfo __ssr={__ssr} />;
        }

        return (
            <div style={style} className={clsx(STYLE.root, className)} ref={ref}>
                {!isBuyer && (
                    <DefaultPromoInfo enableContent={enableContent} content={content} />
                )}
                {isBuyer && (
                    <BuyerPromoInfo
                        promos={promoInfo}
                        enableContent={enableContent}
                        content={content}
                        highlightColor={highlightColor}
                    />
                )}
            </div>
        );
    }
);

ProductPromoInfo.displayName = 'ProductPromoInfo';
